import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Zap, Wifi, Smartphone, Shield, CreditCard, 
  ChevronRight, Star, ExternalLink, TrendingDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PublicLayout from "@/components/layout/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  "strom": Zap,
  "dsl-internet": Wifi,
  "handyvertrag": Smartphone,
  "versicherungen": Shield,
  "kredit-kreditkarte": CreditCard,
};

const categoryColors = {
  "strom": "from-yellow-400 to-orange-500",
  "dsl-internet": "from-blue-400 to-indigo-500",
  "handyvertrag": "from-pink-400 to-rose-500",
  "versicherungen": "from-emerald-400 to-teal-500",
  "kredit-kreditkarte": "from-purple-400 to-violet-500",
};

export default function AllCategoriesPage() {
  const [categoriesData, setCategoriesData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/public/all-categories-tariffs`);
        setCategoriesData(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleTariffClick = async (tariffId, categoryId) => {
    try {
      await axios.post(`${API}/analytics/click?tariff_id=${tariffId}&category_id=${categoryId}`);
    } catch (error) {
      console.error("Error tracking click:", error);
    }
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-12 lg:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mb-4">
              Alle Kategorien im Überblick
            </h1>
            <p className="text-lg text-slate-600">
              Vergleichen Sie Tarife aus allen Kategorien und finden Sie das beste Angebot für Ihre Bedürfnisse.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="space-y-8">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-64 bg-slate-100 rounded-2xl animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {categoriesData.map((category) => {
                const Icon = categoryIcons[category.slug] || Zap;
                const colorGradient = categoryColors[category.slug] || "from-slate-400 to-slate-500";
                
                return (
                  <div key={category.id} className="space-y-6" data-testid={`category-section-${category.slug}`}>
                    {/* Category Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorGradient} flex items-center justify-center`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="font-heading text-2xl font-bold text-navy-900">
                            {category.name}
                          </h2>
                          <p className="text-slate-600">{category.description}</p>
                        </div>
                      </div>
                      <Button variant="outline" asChild className="hidden sm:flex">
                        <Link to={`/${category.slug}`}>
                          Alle {category.name} anzeigen
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                    </div>

                    {/* Tariffs Grid */}
                    {category.tariffs && category.tariffs.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        {category.tariffs.map((tariff) => (
                          <Card 
                            key={tariff.id} 
                            className="border-0 shadow-card hover:shadow-card-hover transition-all duration-300 tariff-card"
                          >
                            <CardContent className="p-4">
                              {/* Provider */}
                              <div className="flex items-center gap-2 mb-3">
                                {tariff.provider?.logo_url ? (
                                  <img 
                                    src={tariff.provider.logo_url} 
                                    alt={tariff.provider.name}
                                    className="w-8 h-8 object-contain rounded"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                                    <span className="text-xs font-bold text-slate-500">
                                      {tariff.provider?.name?.charAt(0) || '?'}
                                    </span>
                                  </div>
                                )}
                                <span className="text-sm font-medium text-slate-700 truncate">
                                  {tariff.provider?.name}
                                </span>
                              </div>

                              {/* Tariff Name */}
                              <h3 className="font-semibold text-navy-900 mb-2 line-clamp-2 min-h-[48px]">
                                {tariff.name}
                              </h3>

                              {/* Badges */}
                              <div className="flex flex-wrap gap-1 mb-3">
                                {tariff.is_testsieger && (
                                  <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                                    Testsieger
                                  </Badge>
                                )}
                                {tariff.is_featured && !tariff.is_testsieger && (
                                  <Badge className="bg-electric-100 text-electric-700 text-xs">
                                    Empfohlen
                                  </Badge>
                                )}
                              </div>

                              {/* Price */}
                              <div className="mb-3">
                                <div className="flex items-baseline gap-1">
                                  <span className="text-2xl font-bold text-navy-900">
                                    {tariff.price?.toFixed(2).replace('.', ',')}
                                  </span>
                                  <span className="text-sm text-slate-500">
                                    {tariff.price_unit}
                                  </span>
                                </div>
                                {tariff.bonus && (
                                  <p className="text-xs text-signal-600 font-medium mt-1">
                                    {tariff.bonus}
                                  </p>
                                )}
                              </div>

                              {/* Rating */}
                              <div className="flex items-center gap-1 mb-4">
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                <span className="text-sm font-medium">{tariff.rating?.toFixed(1)}</span>
                                <span className="text-xs text-slate-500">
                                  ({tariff.rating_count?.toLocaleString('de-DE')})
                                </span>
                              </div>

                              {/* CTA Button */}
                              <Button 
                                size="sm"
                                className="w-full bg-electric-500 hover:bg-electric-600 text-white"
                                onClick={() => {
                                  handleTariffClick(tariff.id, category.id);
                                  if (tariff.affiliate_link) {
                                    window.open(tariff.affiliate_link, '_blank');
                                  }
                                }}
                                data-testid={`tariff-cta-${tariff.id}`}
                              >
                                <ExternalLink className="w-4 h-4 mr-1" />
                                Zum Anbieter
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        Keine Tarife in dieser Kategorie verfügbar.
                      </div>
                    )}

                    {/* Mobile CTA */}
                    <div className="sm:hidden">
                      <Button variant="outline" className="w-full" asChild>
                        <Link to={`/${category.slug}`}>
                          Alle {category.name} anzeigen
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-slate-200"></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 bg-navy-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
            Noch nicht fündig geworden?
          </h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            Nutzen Sie unseren detaillierten Vergleich in jeder Kategorie, um den perfekten Tarif für Ihre Bedürfnisse zu finden.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {categoriesData.slice(0, 3).map((cat) => (
              <Button 
                key={cat.id}
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                asChild
              >
                <Link to={`/${cat.slug}`}>
                  {cat.name} vergleichen
                </Link>
              </Button>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
